/**
 * components/EmptyState
 * ──────────────────────────────────────────────────────────────────────────
 * The educational panel every Finance screen shows when it is empty or gated.
 * One component, one configuration per screen (each screen owns its copy) — the
 * design uses the identical structure on all of them:
 *
 *   1. eyebrow · 17px title · one-line blurb        (24/26/20 padding)
 *   2. "How the data flows" strip + a note          (#FAFAFA, hairlines)
 *   3. three numbered steps in a hairline grid
 *   4. EITHER an amber gate bar OR a footer with
 *      "See a filled example" and the primary action
 *
 * An empty screen in this product is the best teaching moment it gets, which
 * is why it explains the flow rather than saying "No data".
 * ──────────────────────────────────────────────────────────────────────────
 */
import { StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { sans, size as fontSize, leading, type as textRole } from '../../theme/typography';
import type { GateKey } from '../../lib/gate';
import Button from '../Button';
import Eyebrow from '../Eyebrow';
import GateBar from '../GateBar';
import FlowStrip, { type FlowNode } from './FlowStrip';
import NumberedSteps, { type Step } from './NumberedSteps';

export interface EmptyStateProps {
  eyebrow: string;
  title: string;
  blurb: string;
  nodes: readonly FlowNode[];
  /** The sentence under the flow strip. */
  flowNote: string;
  steps: readonly Step[];

  /** When set, the panel ends in a gate bar instead of a footer. */
  gate?: GateKey;
  onResolveGate?: () => void;
  gateNote?: string;

  /** Footer, shown when there is no gate. */
  footNote?: string;
  /** The screen's own primary action — "Assign cash", "Record a count". */
  ctaLabel?: string;
  onCta?: () => void;
  /** Loads the sample org. Omitted in production builds of the shell. */
  onSeeExample?: () => void;
}

export function EmptyState({
  eyebrow,
  title,
  blurb,
  nodes,
  flowNote,
  steps,
  gate,
  onResolveGate,
  gateNote,
  footNote,
  ctaLabel,
  onCta,
  onSeeExample,
}: EmptyStateProps) {
  return (
    <View style={styles.panel}>
      <View style={styles.intro}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <Text style={[textRole.emptyTitle, styles.title]}>{title}</Text>
        <Text style={styles.blurb}>{blurb}</Text>
      </View>

      <View style={styles.flow}>
        <Eyebrow>How the data flows</Eyebrow>
        <View style={styles.flowStrip}>
          <FlowStrip nodes={nodes} />
        </View>
        <Text style={styles.flowNote}>{flowNote}</Text>
      </View>

      <NumberedSteps steps={steps} />

      {gate && onResolveGate ? (
        <GateBar gate={gate} onResolve={onResolveGate} note={gateNote} inCard />
      ) : (
        <View style={styles.footer}>
          {!!footNote && <Text style={styles.footNote}>{footNote}</Text>}
          <View style={styles.footerActions}>
            {!!onSeeExample && (
              <Button label="See a filled example" size="modal" variant="outline" onPress={onSeeExample} />
            )}
            {!!ctaLabel && !!onCta && (
              <Button label={ctaLabel} size="modal" variant="primary" onPress={onCta} />
            )}
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    borderWidth: 1,
    borderColor: neutral.border,
    borderRadius: radii.cardLg,
    backgroundColor: neutral.surface,
    overflow: 'hidden',
  },
  intro: { paddingTop: 24, paddingHorizontal: 26, paddingBottom: 20, gap: 5 },
  title: { marginTop: 3 },
  // The blurb is capped at 580px: a one-line explanation stretched across a
  // 1240px page stops reading as one line.
  blurb: {
    ...sans('400'),
    fontSize: fontSize.button,
    lineHeight: leading(fontSize.button),
    color: neutral.grey600,
    maxWidth: 580,
  },
  flow: {
    paddingTop: 20,
    paddingHorizontal: 26,
    paddingBottom: 22,
    backgroundColor: neutral.surface2,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: neutral.ruleLight,
  },
  flowStrip: { marginTop: 14 },
  flowNote: {
    ...sans('400'),
    fontSize: fontSize.meta,
    lineHeight: leading(fontSize.meta),
    color: neutral.grey600,
    marginTop: 14,
    maxWidth: 620,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    paddingVertical: 16,
    paddingHorizontal: 26,
    borderTopWidth: 1,
    borderTopColor: neutral.ruleLight,
  },
  footNote: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey400, flexShrink: 1 },
  footerActions: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
});

export default EmptyState;
export type { FlowNode, Step };
